import { useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useNavigate } from 'react-router-dom';
import { useMutation, useQuery } from '@tanstack/react-query';
import api from '../../services/api';
import { remediationQueries, triageQueries, TriageSuggestion } from '../../lib/queries';
import { useAuth } from '../../context/AuthContext';
import { useTheme as useCustomTheme } from '../../context/ThemeContext';
import { T, sans, card, topBtn, linkBtn } from '../Vendors/VendorDetails';
import { VendorLogo } from '../VendorRequests/VendorRequests';
import RequestRemediationModal from './RequestRemediationModal';
import RemediationTable, { DueCell, OpenRiskCounts, exportRequests } from './RemediationTable';
import {
  RemediationDetailPayload, RemediationStatus, RemediationSummary, RequestStepper, STATUS_META, SeverityDot, StatusPill,
  apiError, daysUntil, palette, relative, scoreColor,
} from './remediationShared';

type View = 'active' | 'review' | 'overdue' | 'completed' | 'archived' | 'all';
type SortKey = 'activity' | 'due' | 'open' | 'oldest';
type DueFilter = '' | 'overdue' | 'week' | 'none';

const EMPTY: RemediationSummary[] = [];
const ACTIVE: RemediationStatus[] = ['in_progress', 'awaiting_review'];
const PAGE_SIZE = 25;
const APP_HEADER_HEIGHT = 64;
const PANEL_SLIDE_MS = 220;
const DAY_MS = 86_400_000;

/** Main-sidebar "Remediation": every request sent to every vendor. Lean list page: KPI tiles, views, filters, table, and a
 *  quick-view drawer; the full request opens on its own page. */
export default function Remediation() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { mode } = useCustomTheme();
  const isDark = mode === 'dark';
  const c = palette(isDark);
  const canAct = !!user && user.role !== 'viewer';

  const listQuery = useQuery(remediationQueries.list());
  const requests = (listQuery.data?.requests ?? EMPTY) as RemediationSummary[];
  const suggestionsQuery = useQuery(triageQueries.list());
  const suggestions = suggestionsQuery.data ?? [];
  const [approving, setApproving] = useState<TriageSuggestion | null>(null);
  const [suggestionsCollapsed, setSuggestionsCollapsed] = useState(false);
  const dismissSuggestion = useMutation({
    mutationFn: (id: string) => api.post(`/v1/triage-suggestions/${id}/dismiss`),
    meta: { invalidates: [['triage-suggestions']] },
  });

  const [view, setView] = useState<View>('active');
  // A tile is highlighted only after the user picks it, never by default.
  const [pickedTile, setPickedTile] = useState<string | null>(null);
  const [search, setSearch] = useState('');
  const [vendorFilter, setVendorFilter] = useState('');
  const [severityFilter, setSeverityFilter] = useState<'' | 'Critical' | 'High' | 'Medium'>('');
  const [ownerFilter, setOwnerFilter] = useState('');
  const [dueFilter, setDueFilter] = useState<DueFilter>('');
  const [sort, setSort] = useState<SortKey>('activity');
  const [page, setPage] = useState(1);
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [creating, setCreating] = useState(false);
  const [toast, setToast] = useState<{ text: string; tone: 'ok' | 'error' } | null>(null);
  const [panelId, setPanelId] = useState<string | null>(null);
  const [panelVisible, setPanelVisible] = useState(false);
  const panelTimer = useRef<number | undefined>(undefined);

  const showToast = (text: string, tone: 'ok' | 'error' = 'ok') => {
    setToast({ text, tone });
    window.setTimeout(() => setToast(null), 4500);
  };
  const post = useMutation({
    mutationFn: async ({ id, path }: { id: string; path: string }) => (await api.post(`/v1/remediation/${id}/${path}`, {})).data,
    meta: { invalidates: [['remediation']] },
  });

  /* ─── Derived ─── */

  const active = requests.filter(r => ACTIVE.includes(r.status));
  const review = requests.filter(r => r.status === 'awaiting_review');
  const overdue = requests.filter(r => r.overdue);
  const completed = requests.filter(r => r.status === 'completed');
  const archived = requests.filter(r => r.status === 'archived');
  const openRisks = active.reduce((n, r) => n + r.open_risk_count, 0);
  const openCritical = active.reduce((n, r) => n + (r.open_by_severity?.Critical ?? 0), 0);
  const openHigh = active.reduce((n, r) => n + (r.open_by_severity?.High ?? 0), 0);
  const durations = completed.filter(r => r.completed_at && r.created_at)
    .map(r => (new Date(r.completed_at!).getTime() - new Date(r.created_at!).getTime()) / DAY_MS);
  const avgDays = durations.length ? durations.reduce((a, b) => a + b, 0) / durations.length : null;
  const waitingOnVendor = requests.filter(r => r.status === 'in_progress').length;

  const views: { key: View; label: string; count: number; alert?: boolean }[] = [
    { key: 'active', label: 'Active', count: active.length },
    { key: 'review', label: 'Awaiting review', count: review.length, alert: review.length > 0 },
    { key: 'overdue', label: 'Overdue', count: overdue.length, alert: overdue.length > 0 },
    { key: 'completed', label: 'Completed', count: completed.length },
    { key: 'archived', label: 'Archived', count: archived.length },
    { key: 'all', label: 'All', count: requests.length },
  ];
  const base = { active, review, overdue, completed, archived, all: requests }[view];

  const vendorOptions = useMemo(() => Array.from(new Map(requests.map(r => [r.vendor_id, r.vendor_name || 'Vendor'])).entries())
    .sort((a, b) => a[1].localeCompare(b[1])), [requests]);
  const ownerOptions = useMemo(() => Array.from(new Set(requests.map(r => r.created_by_name).filter(Boolean) as string[])).sort(), [requests]);

  const q = search.trim().toLowerCase();
  const filtered = base.filter(r =>
    (!vendorFilter || r.vendor_id === vendorFilter) &&
    (!severityFilter || (r.open_by_severity?.[severityFilter] ?? 0) > 0) &&
    (!ownerFilter || r.created_by_name === ownerFilter) &&
    (!dueFilter || (dueFilter === 'overdue' ? r.overdue : dueFilter === 'none' ? !r.due_date
      : !!r.due_date && r.status === 'in_progress' && daysUntil(r.due_date) >= 0 && daysUntil(r.due_date) <= 7)) &&
    (!q || [r.title, r.reference, r.vendor_name, r.vendor_domain, r.created_by_name, ...r.categories, ...r.recipients]
      .some(f => f?.toLowerCase().includes(q))));
  const sorted = [...filtered].sort((a, b) => {
    switch (sort) {
      case 'due': return (a.due_date || '9999').localeCompare(b.due_date || '9999');
      case 'open': return b.open_risk_count - a.open_risk_count || (b.open_by_severity?.Critical ?? 0) - (a.open_by_severity?.Critical ?? 0);
      case 'oldest': return (a.created_at || '').localeCompare(b.created_at || '');
      default: return (b.updated_at || '').localeCompare(a.updated_at || '');
    }
  });
  const totalPages = Math.max(1, Math.ceil(sorted.length / PAGE_SIZE));
  const currentPage = Math.min(page, totalPages);
  const pageRows = sorted.slice((currentPage - 1) * PAGE_SIZE, currentPage * PAGE_SIZE);
  const filtersActive = !!(q || vendorFilter || severityFilter || ownerFilter || dueFilter);
  const selectedRows = requests.filter(r => selected.has(r.id));
  const remindable = selectedRows.filter(r => r.status === 'in_progress' && r.open_risk_count > 0);
  const archivable = selectedRows.filter(r => r.status !== 'archived');

  useEffect(() => { setPage(1); }, [view, q, vendorFilter, severityFilter, ownerFilter, dueFilter, sort]);

  const changeView = (next: View, tile: string | null = null) => { setView(next); setSelected(new Set()); setPickedTile(tile); };
  const clearFilters = () => { setSearch(''); setVendorFilter(''); setSeverityFilter(''); setOwnerFilter(''); setDueFilter(''); };

  /* ─── Drawer ─── */

  const openPanel = (id: string) => { window.clearTimeout(panelTimer.current); setPanelId(id); setPanelVisible(true); };
  const closePanel = () => {
    setPanelVisible(false);
    window.clearTimeout(panelTimer.current);
    panelTimer.current = window.setTimeout(() => setPanelId(null), PANEL_SLIDE_MS);
  };
  useEffect(() => () => window.clearTimeout(panelTimer.current), []);
  const panelRow = requests.find(r => r.id === panelId) || null;

  /* ─── Bulk ─── */

  const runBulk = async (rows: RemediationSummary[], path: 'remind' | 'archive', verb: string) => {
    const results = await Promise.allSettled(rows.map(r => post.mutateAsync({ id: r.id, path })));
    const done = results.filter(x => x.status === 'fulfilled').length;
    const firstError = results.find(x => x.status === 'rejected') as PromiseRejectedResult | undefined;
    setSelected(new Set());
    if (done === results.length) showToast(`${verb} ${done} request${done === 1 ? '' : 's'}.`);
    else showToast(`${verb} ${done} of ${results.length}. ${apiError(firstError?.reason, '')}`.trim(), done ? 'ok' : 'error');
  };

  const inputStyle: React.CSSProperties = {
    padding: '7px 10px', fontSize: 12.5, fontFamily: sans, color: c.ink, background: c.surface,
    border: `1px solid ${c.line}`, borderRadius: 6, outline: 'none', boxSizing: 'border-box', cursor: 'pointer',
  };

  const kpis: { label: string; value: React.ReactNode; color: string; sub: string; subColor?: string; to: View }[] = [
    { label: 'Active requests', value: active.length, color: c.ink, sub: `${waitingOnVendor} waiting on vendors`, to: 'active' },
    { label: 'Awaiting your review', value: review.length, color: review.length ? c.amber : c.ink,
      sub: review.length ? 'submitted by vendors' : 'nothing waiting on you', to: 'review' },
    { label: 'Overdue', value: overdue.length, color: overdue.length ? c.danger : c.ink,
      sub: overdue.length ? 'past their due date' : 'all on schedule', subColor: overdue.length ? c.danger : undefined, to: 'overdue' },
    { label: 'Open risks', value: openRisks, color: openCritical ? c.danger : c.ink,
      sub: openRisks ? `${openCritical} critical · ${openHigh} high` : 'none open', to: 'active' },
    { label: 'Avg. time to complete', value: avgDays === null ? '—' : avgDays < 1 ? '<1 day' : `${Math.round(avgDays)} day${Math.round(avgDays) === 1 ? '' : 's'}`, color: c.ink,
      sub: durations.length ? `across ${durations.length} completed` : 'no completed requests yet', to: 'completed' },
  ];

  const emptyState = () => {
    if (requests.length === 0) {
      return (
        <div style={{ padding: '56px 24px', textAlign: 'center' }}>
          <div style={{ fontSize: 15, fontWeight: 600, color: c.ink, marginBottom: 6 }}>No remediation requests yet</div>
          <div style={{ fontSize: 12.5, color: c.slate, maxWidth: 460, margin: '0 auto', lineHeight: 1.6 }}>
            Pick the risks you want a vendor to fix. Their contacts get an invitation to the vendor portal, and later scans confirm each fix on their own.
          </div>
          {canAct && <button style={{ ...topBtn(true, isDark), margin: '16px auto 0' }} onClick={() => setCreating(true)}>+ New request</button>}
        </div>
      );
    }
    return (
      <div style={{ padding: '48px 24px', textAlign: 'center' }}>
        <div style={{ fontSize: 14, fontWeight: 600, color: c.ink, marginBottom: 4 }}>
          {filtersActive ? 'No requests match these filters' : `Nothing ${view === 'review' ? 'awaiting your review' : view === 'overdue' ? 'overdue' : `in ${views.find(v => v.key === view)?.label}`}`}
        </div>
        {filtersActive && <button style={linkBtn(isDark)} onClick={clearFilters}>Clear filters</button>}
      </div>
    );
  };

  return (
    <div style={{ fontFamily: sans, background: c.bg, minHeight: '100vh' }}>
      <div style={{
        padding: '16px 24px', borderBottom: `1px solid ${c.line}`, background: c.surface,
        display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 12,
      }}>
        <div>
          <h1 style={{ fontSize: 18, fontWeight: 700, color: c.ink, margin: 0 }}>Remediation</h1>
          <p style={{ fontSize: 12, color: c.muted, margin: '2px 0 0' }}>Every request asking a vendor to fix risks. Scans confirm each fix on their own.</p>
        </div>
        <div style={{ display: 'flex', gap: 8 }}>
          <button style={{ ...topBtn(false, isDark), opacity: sorted.length ? 1 : 0.5 }} disabled={!sorted.length}
            onClick={() => exportRequests(sorted, `remediation-${new Date().toISOString().slice(0, 10)}.csv`)}>Export CSV</button>
          {canAct && <button style={topBtn(true, isDark)} onClick={() => setCreating(true)}>+ New request</button>}
        </div>
      </div>

      <div style={{ padding: '20px 24px', display: 'grid', gap: 16 }}>
        {listQuery.isError && (
          <div style={{ ...card(isDark), color: c.danger, fontSize: 12.5 }}>
            {apiError(listQuery.error, 'Could not load remediation requests.')} <button style={linkBtn(isDark)} onClick={() => listQuery.refetch()}>Retry</button>
          </div>
        )}

        {suggestions.length > 0 && (
          <div style={{ ...card(isDark), padding: 0, overflow: 'hidden', borderColor: isDark ? '#3730a3' : '#C7D2FE' }}>
            <button onClick={() => setSuggestionsCollapsed(v => !v)} style={{
              width: '100%', display: 'flex', alignItems: 'center', gap: 10, padding: '12px 16px', border: 'none', cursor: 'pointer',
              background: isDark ? '#1e1b4b' : '#EEF2FF', fontFamily: sans, textAlign: 'left',
            }}>
              <span style={{ fontSize: 14 }}>✦</span>
              <span style={{ fontSize: 12.5, fontWeight: 700, color: isDark ? '#c7d2fe' : '#4338CA' }}>
                Suggested by FluxWatch AI · {suggestions.length} vendor{suggestions.length === 1 ? '' : 's'}
              </span>
              <span style={{ marginLeft: 'auto', fontSize: 12, color: isDark ? '#a5b4fc' : '#6366F1', transform: suggestionsCollapsed ? 'rotate(-90deg)' : 'none', transition: 'transform .12s' }}>▾</span>
            </button>
            {!suggestionsCollapsed && suggestions.map(s => (
              <div key={s.id} style={{ display: 'flex', alignItems: 'flex-start', gap: 12, padding: '14px 16px', borderTop: `1px solid ${c.line}` }}>
                <VendorLogo name={s.vendor_name || '?'} domain={s.vendor_domain || ''} size={30} isDark={isDark} />
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                    <span style={{ fontSize: 13, fontWeight: 600, color: c.ink }}>{s.vendor_name}</span>
                    <span style={{ fontSize: 11, color: c.muted }}>{s.finding_count} finding{s.finding_count === 1 ? '' : 's'} · {s.groups.length} group{s.groups.length === 1 ? '' : 's'}</span>
                  </div>
                  <div style={{ fontSize: 12.5, color: c.ink, marginTop: 3, fontWeight: 600 }}>{s.title}</div>
                  {s.summary && <div style={{ fontSize: 12, color: c.slate, marginTop: 3, lineHeight: 1.5 }}>{s.summary}</div>}
                </div>
                {canAct && (
                  <div style={{ display: 'flex', gap: 8, flexShrink: 0 }}>
                    <button onClick={() => setApproving(s)} style={{ ...topBtn(true, isDark), padding: '6px 11px', fontSize: 12 }}>Create request</button>
                    <button onClick={() => dismissSuggestion.mutate(s.id)} disabled={dismissSuggestion.isPending}
                      style={{ ...topBtn(false, isDark), padding: '6px 11px', fontSize: 12, opacity: dismissSuggestion.isPending ? 0.6 : 1 }}>Dismiss</button>
                  </div>
                )}
              </div>
            ))}
          </div>
        )}

        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))', gap: 12 }}>
          {kpis.map(k => (
            <button key={k.label} onClick={() => changeView(k.to, pickedTile === k.label ? null : k.label)} style={{
              ...card(isDark), textAlign: 'left', cursor: 'pointer', fontFamily: sans, padding: '14px 16px',
              boxShadow: pickedTile === k.label && view === k.to ? `inset 0 0 0 1.5px ${c.ink}` : 'none',
            }}>
              <div style={{ fontSize: 10.5, fontWeight: 700, color: c.muted, textTransform: 'uppercase', letterSpacing: '.05em' }}>{k.label}</div>
              <div style={{ fontSize: 26, fontWeight: 700, color: k.color, marginTop: 6, lineHeight: 1 }}>{listQuery.isPending ? '…' : k.value}</div>
              <div style={{ fontSize: 11.5, color: k.subColor || c.muted, marginTop: 6, lineHeight: 1.35, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{k.sub}</div>
            </button>
          ))}
        </div>

        <div style={{ ...card(isDark), padding: 0, overflow: 'hidden' }}>
          <div role="tablist" style={{ display: 'flex', gap: 4, padding: '10px 12px', borderBottom: `1px solid ${c.line}`, overflowX: 'auto', scrollbarWidth: 'thin' }}>
            {views.map(v => {
              const isActive = view === v.key;
              return (
                <button key={v.key} role="tab" aria-selected={isActive} onClick={() => changeView(v.key)} style={{
                  display: 'inline-flex', alignItems: 'center', gap: 6, padding: '6px 11px', borderRadius: 99, border: 'none',
                  cursor: 'pointer', fontFamily: sans, fontSize: 12.5, fontWeight: 600, whiteSpace: 'nowrap',
                  background: isActive ? (isDark ? '#e2e8f0' : T.ink) : 'transparent', color: isActive ? (isDark ? '#0d0d0d' : '#fff') : c.slate,
                }}>
                  {v.label}
                  <span style={{
                    fontSize: 10.5, fontWeight: 700, padding: '0 6px', borderRadius: 99, lineHeight: '17px',
                    background: v.alert && v.count ? '#DC2626' : isActive ? '#ffffff22' : c.surface2, color: v.alert && v.count ? '#fff' : 'inherit',
                  }}>{v.count}</span>
                </button>
              );
            })}
          </div>

          <div style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '10px 12px', borderBottom: `1px solid ${c.line}`, flexWrap: 'wrap' }}>
            <input value={search} onChange={e => setSearch(e.target.value)} placeholder="Search request, REM-…, vendor, contact"
              aria-label="Search requests" style={{ ...inputStyle, cursor: 'text', width: 250, maxWidth: '100%' }} />
            <select aria-label="Vendor" value={vendorFilter} onChange={e => setVendorFilter(e.target.value)} style={inputStyle}>
              <option value="">All vendors</option>
              {vendorOptions.map(([id, name]) => <option key={id} value={id}>{name}</option>)}
            </select>
            <select aria-label="Open severity" value={severityFilter} onChange={e => setSeverityFilter(e.target.value as typeof severityFilter)} style={inputStyle}>
              <option value="">Any open severity</option>
              <option value="Critical">Has open critical</option>
              <option value="High">Has open high</option>
              <option value="Medium">Has open medium</option>
            </select>
            <select aria-label="Due" value={dueFilter} onChange={e => setDueFilter(e.target.value as DueFilter)} style={inputStyle}>
              <option value="">Any due date</option>
              <option value="overdue">Overdue</option>
              <option value="week">Due within 7 days</option>
              <option value="none">No due date</option>
            </select>
            {ownerOptions.length > 1 && (
              <select aria-label="Requested by" value={ownerFilter} onChange={e => setOwnerFilter(e.target.value)} style={inputStyle}>
                <option value="">Anyone</option>
                {ownerOptions.map(o => <option key={o} value={o}>{o}</option>)}
              </select>
            )}
            <select aria-label="Sort" value={sort} onChange={e => setSort(e.target.value as SortKey)} style={inputStyle}>
              <option value="activity">Recent activity</option>
              <option value="due">Due soonest</option>
              <option value="open">Most open risks</option>
              <option value="oldest">Oldest first</option>
            </select>
            {filtersActive && <button style={linkBtn(isDark)} onClick={clearFilters}>Clear filters</button>}
            <span style={{ marginLeft: 'auto', fontSize: 12, color: c.muted }}>{sorted.length} {sorted.length === 1 ? 'request' : 'requests'}</span>
          </div>

          {selected.size > 0 && (
            <div style={{ display: 'flex', alignItems: 'center', gap: 14, padding: '8px 12px', borderBottom: `1px solid ${c.line}`,
              background: isDark ? '#0a1628' : T.blueBg, fontSize: 12.5, color: c.ink, flexWrap: 'wrap' }}>
              <strong>{selected.size} selected</strong>
              <button style={{ ...linkBtn(isDark), opacity: remindable.length ? 1 : 0.5 }} disabled={!remindable.length || post.isPending}
                title={remindable.length ? undefined : 'Only requests waiting on the vendor with open risks'}
                onClick={() => runBulk(remindable, 'remind', 'Reminded')}>Send reminder{remindable.length ? ` (${remindable.length})` : ''}</button>
              <button style={{ ...linkBtn(isDark), opacity: archivable.length ? 1 : 0.5 }} disabled={!archivable.length || post.isPending}
                onClick={() => runBulk(archivable, 'archive', 'Archived')}>Archive</button>
              <button style={linkBtn(isDark)} onClick={() => exportRequests(selectedRows, `remediation-selected-${new Date().toISOString().slice(0, 10)}.csv`)}>Export</button>
              <button style={{ ...linkBtn(isDark), color: c.muted }} onClick={() => setSelected(new Set())}>Clear selection</button>
            </div>
          )}

          {listQuery.isPending ? (
            <div style={{ padding: 40, textAlign: 'center', color: c.muted, fontSize: 13 }}>Loading requests…</div>
          ) : sorted.length === 0 ? emptyState() : (
            <>
              <RemediationTable rows={pageRows} isDark={isDark} showVendor selectable={canAct} selected={selected}
                onToggle={(id, on) => setSelected(prev => { const next = new Set(prev); if (on) next.add(id); else next.delete(id); return next; })}
                onToggleAll={on => setSelected(prev => { const next = new Set(prev); pageRows.forEach(r => (on ? next.add(r.id) : next.delete(r.id))); return next; })}
                onOpen={r => (panelId === r.id && panelVisible ? closePanel() : openPanel(r.id))}
                activeId={panelVisible ? panelId : null} />
              {totalPages > 1 && (
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '10px 14px', fontSize: 12, color: c.slate }}>
                  <span>Showing {(currentPage - 1) * PAGE_SIZE + 1}–{Math.min(currentPage * PAGE_SIZE, sorted.length)} of {sorted.length}</span>
                  <span style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                    <button style={{ ...topBtn(false, isDark), opacity: currentPage === 1 ? 0.5 : 1 }} disabled={currentPage === 1} onClick={() => setPage(currentPage - 1)}>‹ Prev</button>
                    Page {currentPage} of {totalPages}
                    <button style={{ ...topBtn(false, isDark), opacity: currentPage === totalPages ? 0.5 : 1 }} disabled={currentPage === totalPages} onClick={() => setPage(currentPage + 1)}>Next ›</button>
                  </span>
                </div>
              )}
            </>
          )}
        </div>
      </div>

      {panelRow && (
        <RequestPanel r={panelRow} isDark={isDark} visible={panelVisible} canAct={canAct} busy={post.isPending} onClose={closePanel}
          onOpen={() => navigate(`/remediation/${panelRow.id}`)}
          onAction={(path, verb) => post.mutate({ id: panelRow.id, path }, {
            onSuccess: () => showToast(verb),
            onError: err => showToast(apiError(err, 'That did not work.'), 'error'),
          })} />
      )}

      {creating && (
        <RequestRemediationModal isDark={isDark} onClose={() => setCreating(false)}
          onCreated={id => { setCreating(false); navigate(`/remediation/${id}`); }} />
      )}

      {approving && (
        <RequestRemediationModal isDark={isDark} vendor={{ id: approving.vendor_id, name: approving.vendor_name || 'Vendor', domain: approving.vendor_domain }}
          preselected={approving.indicator_names} defaultTitle={approving.title} defaultMessage={approving.summary ?? undefined}
          defaultDueDate={approving.suggested_due_date} triageSuggestionId={approving.id}
          onClose={() => setApproving(null)}
          onCreated={id => { setApproving(null); navigate(`/remediation/${id}`); }} />
      )}

      {toast && createPortal(
        <div role="status" style={{ position: 'fixed', bottom: 20, right: 20, zIndex: 1400, padding: '10px 14px', borderRadius: 8, fontSize: 12.5, fontFamily: sans,
          background: c.surface, color: c.ink, border: `1px solid ${c.line}`, boxShadow: isDark ? '0 8px 30px rgba(0,0,0,.8)' : '0 8px 24px rgba(15,23,42,.12)' }}>
          <span style={{ color: toast.tone === 'ok' ? T.grn : c.danger, fontWeight: 700, marginRight: 8 }}>{toast.tone === 'ok' ? '✓' : '!'}</span>{toast.text}
        </div>,
        document.body,
      )}
    </div>
  );
}

/* ─── Quick-view drawer ─────────────────────────────────────────────── */

function RequestPanel({ r, isDark, visible, canAct, busy, onClose, onOpen, onAction }: {
  r: RemediationSummary;
  isDark: boolean;
  visible: boolean;
  canAct: boolean;
  busy: boolean;
  onClose: () => void;
  onOpen: () => void;
  onAction: (path: string, doneMessage: string) => void;
}) {
  const c = palette(isDark);
  const panelRef = useRef<HTMLElement>(null);
  const detail = useQuery(remediationQueries.detail(r.id)).data as RemediationDetailPayload | undefined;

  useEffect(() => {
    const onDown = (e: MouseEvent) => {
      const target = e.target as HTMLElement;
      if (panelRef.current?.contains(target) || target.closest('[data-remediation-row], [role="dialog"]')) return;
      onClose();
    };
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose(); };
    document.addEventListener('mousedown', onDown);
    document.addEventListener('keydown', onKey);
    return () => { document.removeEventListener('mousedown', onDown); document.removeEventListener('keydown', onKey); };
  }, [onClose]);

  const label: React.CSSProperties = { fontSize: 9.5, fontWeight: 700, letterSpacing: '.08em', textTransform: 'uppercase', color: c.muted };
  const section: React.CSSProperties = { padding: '16px 20px', borderBottom: `1px solid ${c.line}` };
  const btn: React.CSSProperties = {
    fontFamily: sans, fontSize: 12, fontWeight: 600, padding: '9px 12px', borderRadius: 6, border: `1px solid ${c.line}`,
    background: c.surface, color: c.ink, cursor: 'pointer', flex: 1,
  };
  const fact = (name: string, value: React.ReactNode) => (
    <div><div style={label}>{name}</div><div style={{ fontSize: 12.5, color: c.ink, marginTop: 4 }}>{value}</div></div>
  );
  const openRisks = (detail?.risks ?? []).filter(x => x.status === 'open');
  const lastMessage = detail?.messages[detail.messages.length - 1];
  const remindedToday = !!r.last_reminder_at && new Date(r.last_reminder_at).toDateString() === new Date().toDateString();

  const secondary: { label: string; path: string; done: string; disabled?: boolean; title?: string }[] = [];
  if (r.status === 'in_progress') secondary.push({ label: remindedToday ? 'Reminded today' : 'Send reminder', path: 'remind', done: 'Reminder sent to the vendor.',
    disabled: remindedToday || !r.open_risk_count, title: !r.open_risk_count ? 'Every risk is already fixed' : undefined });
  if (r.status === 'awaiting_review') secondary.push({ label: 'Mark completed', path: 'complete', done: 'Marked completed. The vendor was emailed.' });
  if (r.status === 'completed') secondary.push({ label: 'Reopen', path: 'reopen', done: 'Reopened and sent back to the vendor.' });
  if (r.status !== 'archived') secondary.push({ label: 'Archive', path: 'archive', done: 'Archived.' });
  else secondary.push({ label: 'Unarchive', path: 'unarchive', done: 'Unarchived.' });

  return createPortal(
    <aside ref={panelRef} data-remediation-panel role="dialog" aria-label={`${r.title} summary`} style={{
      position: 'fixed', top: APP_HEADER_HEIGHT, right: 0, bottom: 0, width: 'min(440px, 100vw)', zIndex: 1150, background: c.surface,
      borderLeft: `1px solid ${c.line}`, display: 'flex', flexDirection: 'column', fontFamily: sans,
      animation: `${visible ? 'remPanelIn' : 'remPanelOut'} ${PANEL_SLIDE_MS}ms ${visible ? 'ease-out' : 'ease-in'} forwards`,
    }}>
      <style>{`
        @keyframes remPanelIn { from { transform: translateX(100%); } to { transform: translateX(0); } }
        @keyframes remPanelOut { from { transform: translateX(0); } to { transform: translateX(100%); } }
        @media (prefers-reduced-motion: reduce) { aside[data-remediation-panel] { animation-duration: 1ms !important; } }
      `}</style>

      <div style={{ padding: '18px 20px 14px', borderBottom: `1px solid ${c.line}` }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <StatusPill status={r.status} isDark={isDark} />
            <span style={{ fontSize: 11, color: c.muted }}>{r.reference}</span>
          </div>
          <button aria-label="Close" onClick={onClose} style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: 18, color: c.muted, lineHeight: 1 }}>×</button>
        </div>
        <div style={{ fontSize: 16, fontWeight: 700, color: c.ink, marginTop: 10, wordBreak: 'break-word' }}>{r.title}</div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginTop: 12 }}>
          <VendorLogo name={r.vendor_name || '?'} domain={r.vendor_domain || ''} size={32} isDark={isDark} />
          <div style={{ minWidth: 0, flex: 1 }}>
            <div style={{ fontSize: 13, fontWeight: 600, color: c.ink }}>{r.vendor_name}</div>
            <div style={{ fontSize: 11.5, color: c.muted }}>{r.vendor_domain}</div>
          </div>
          {r.vendor_score != null && <span style={{ fontSize: 16, fontWeight: 700, color: scoreColor(r.vendor_score, isDark) }}>{r.vendor_score}</span>}
        </div>
      </div>

      <div style={{ flex: 1, overflowY: 'auto' }}>
        <div style={section}><RequestStepper r={r} isDark={isDark} /></div>

        <div style={{ ...section, display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '14px 16px' }}>
          {fact('Due', <DueCell r={r} isDark={isDark} />)}
          {fact('Requested by', r.created_by_name || '—')}
          {fact('Fixed', `${r.fixed_risk_count} of ${r.risk_count} risks`)}
          {fact('Score', <span>
            <strong style={{ color: scoreColor(r.current_score, isDark) }}>{r.current_score ?? '—'}</strong>
            <span style={{ color: c.muted }}> now · {r.initial_score ?? '—'} at request · {r.projected_score ?? '—'} projected</span>
          </span>)}
          {fact('Contacts', r.recipients.length ? r.recipients.join(', ') : '—')}
          {fact('Last activity', relative(r.updated_at))}
        </div>

        <div style={section}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline' }}>
            <span style={label}>Open risks</span>
            <OpenRiskCounts r={r} isDark={isDark} />
          </div>
          {!detail ? <div style={{ fontSize: 12, color: c.muted, marginTop: 10 }}>Loading…</div>
            : openRisks.length === 0 ? <div style={{ fontSize: 12.5, color: c.green, marginTop: 10 }}>The latest scan confirms every risk fixed.</div>
            : (
              <div style={{ marginTop: 8 }}>
                {openRisks.slice(0, 6).map(risk => (
                  <div key={risk.id} style={{ display: 'grid', gridTemplateColumns: '84px 1fr', gap: 10, padding: '7px 0', borderBottom: `1px solid ${c.rowLine}`, fontSize: 12.5 }}>
                    <SeverityDot severity={risk.severity} isDark={isDark} />
                    <span style={{ color: c.ink, minWidth: 0 }}>{risk.name}<span style={{ color: c.muted }}> · {risk.category || 'Other'}</span></span>
                  </div>
                ))}
                {openRisks.length > 6 && <div style={{ fontSize: 11.5, color: c.muted, marginTop: 6 }}>+{openRisks.length - 6} more</div>}
              </div>
            )}
        </div>

        {lastMessage && (
          <div style={{ ...section, borderBottom: 'none' }}>
            <span style={label}>Latest message</span>
            <div style={{ marginTop: 8, padding: '10px 12px', borderRadius: 8, background: c.surface2, border: `1px solid ${c.line}` }}>
              <div style={{ fontSize: 11, color: c.muted, marginBottom: 4 }}>
                {lastMessage.author_name || lastMessage.author_email}{lastMessage.author_type === 'vendor' ? ` · ${r.vendor_name}` : ''} · {relative(lastMessage.created_at)}
              </div>
              <div style={{ fontSize: 12.5, color: c.ink, lineHeight: 1.55, whiteSpace: 'pre-wrap', wordBreak: 'break-word', maxHeight: 120, overflow: 'hidden' }}>{lastMessage.body}</div>
            </div>
          </div>
        )}
      </div>

      <div style={{ padding: '14px 20px', borderTop: `1px solid ${c.line}`, display: 'grid', gap: 8 }}>
        <button onClick={onOpen} style={{ ...btn, flex: 'none', background: c.ink, color: isDark ? '#0d0d0d' : '#fff', border: `1px solid ${c.ink}` }}>Open request</button>
        {canAct && (
          <div style={{ display: 'flex', gap: 8 }}>
            {secondary.map(a => (
              <button key={a.path} style={{ ...btn, opacity: a.disabled || busy ? 0.55 : 1 }} disabled={a.disabled || busy} title={a.title}
                onClick={() => onAction(a.path, a.done)}>{a.label}</button>
            ))}
          </div>
        )}
      </div>
    </aside>,
    document.body,
  );
}
